import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { authService } from "../services/authService";
import { tokenStorage } from "../services/tokenStorage";
import { tenantEntryStorage, STORAGE_KEY as TENANT_ENTRY_STORAGE_KEY } from "../services/tenantEntryStorage";
import { tenantApi } from "../services/tenantApi";
import {
  SECTIONS,
  isManager as rolesIsManager,
  canSeeSection as rolesCanSeeSection,
  isInternalProceduresManager as rolesIsInternalProceduresManager,
  canManageCollection as rolesCanManageCollection,
} from "../utils/roles";
import { folderApi } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // internal_procedures access for nav / home gating: "unknown" until resolved,
  // then true/false. Managers and admin resolve instantly; a bare department
  // member is offered the section only if a folder is bound to a role they hold
  // (the backend already scopes the listing). Shared so the sidebar and the
  // homepage agree and only one lookup runs per session.
  const [internalProceduresAccess, setInternalProceduresAccess] = useState("unknown");
  const [enteredTenant, setEnteredTenant] = useState(() => tenantEntryStorage.get());
  // Mirrors enteredTenant so the storage-event handler below can read the
  // current value without a stale closure, without going through a setState
  // updater function (those should stay pure — React may invoke them more
  // than once, e.g. under StrictMode — and this one needs to trigger a
  // reload() side effect).
  const enteredTenantRef = useRef(enteredTenant);
  useEffect(() => {
    enteredTenantRef.current = enteredTenant;
  }, [enteredTenant]);

  // Check if user is already logged in on app start
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = tokenStorage.getAccessToken();
        if (token) {
          const userInfo = await authService.getCurrentUser();
          // A stored entered_tenant is only meaningful for a super_admin. If
          // the fetched user's roles no longer include super_admin (e.g.
          // revoked in Zitadel since the tenant was entered, or a leftover
          // value survived from before a role change while valid tokens were
          // still present), clear it rather than trusting it — otherwise the
          // UI would show "Managing tenant: X" while every real request is
          // actually scoped to the user's own tenant (RPAA-213 finding #5).
          if (tenantEntryStorage.get() && !userInfo?.roles?.includes("super_admin")) {
            tenantEntryStorage.clear();
            setEnteredTenant(null);
          }
          setUser(userInfo);
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Only a genuine 401 means the stored token is actually invalid.
        // A 500/503/network blip while getCurrentUser is loading must not log
        // out an admin with a perfectly valid session (RPAA-276 HIGH finding:
        // this used to clear on ANY error because authService.getCurrentUser
        // discarded the status code before it got here).
        //
        // 403 is deliberately not here, though get_user_info raises one for a
        // role-less user and that session really is invalid. At this layer it
        // cannot be told apart from a 403 served by something in front of the
        // backend, and Radio Popular self-hosted runs behind proxies, so
        // including it would reopen this very bug for anyone who meets one.
        // Clearing would buy nothing anyway: a 403 here never reaches
        // setIsAuthenticated, so the user lands on the login screen either way
        // and the only difference is a dead string left in localStorage.
        if (error.response?.status === 401) {
          tokenStorage.clearAll();
          tenantEntryStorage.clear();
          setEnteredTenant(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Listen for session-expired events from the token refresh interceptor
  useEffect(() => {
    const handleSessionExpired = () => {
      tokenStorage.clearAll();
      tenantEntryStorage.clear();
      setUser(null);
      setIsAuthenticated(false);
      setEnteredTenant(null);
    };
    window.addEventListener("auth:session-expired", handleSessionExpired);
    return () => window.removeEventListener("auth:session-expired", handleSessionExpired);
  }, []);

  // Cross-tab sync: the request interceptor reads enteredTenant straight from
  // localStorage (outside React), so another tab entering/exiting a tenant
  // must be reflected here too, or this tab's UI (banner, elevated views)
  // goes stale relative to what's actually being sent on the wire. The native
  // storage event only fires in OTHER tabs/windows — the tab that called
  // enterTenant/exitTenant already updated its own state directly.
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === TENANT_ENTRY_STORAGE_KEY || e.key === null) {
        const prev = enteredTenantRef.current;
        const next = tenantEntryStorage.get();
        setEnteredTenant(next);
        if ((prev?.id ?? null) !== (next?.id ?? null)) {
          // Another tab entered/exited/switched tenants. On-screen data in
          // THIS tab (dashboards, configs, chats, etc.) was fetched for the
          // OLD tenant and has no way to know it's now stale, even once the
          // banner and outgoing requests reflect the new one. Rather than
          // making every page's data-fetching effect cross-tab-aware, force
          // a full reload so everything re-fetches consistent with the new
          // tenant context (RPAA-213 finding #4).
          window.location.reload();
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Resolve internal_procedures access whenever the authenticated user changes.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setInternalProceduresAccess(false);
      return undefined;
    }
    const roles = user.roles;
    if (rolesIsInternalProceduresManager(roles)) {
      setInternalProceduresAccess(true);
      return undefined;
    }
    if (!rolesCanManageCollection(roles, SECTIONS.INTERNAL_PROCEDURES)) {
      setInternalProceduresAccess(false);
      return undefined;
    }
    // Department member: confirm at least one bound folder before offering it.
    setInternalProceduresAccess("loading");
    let cancelled = false;
    folderApi
      .listFolders(SECTIONS.INTERNAL_PROCEDURES)
      .then((res) => {
        if (!cancelled) setInternalProceduresAccess((res?.folders?.length ?? res?.total_count ?? 0) > 0);
      })
      .catch(() => {
        if (!cancelled) setInternalProceduresAccess(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user]);

  const login = useCallback(async () => {
    try {
      // Get authorization URL from backend
      const response = await authService.initiateLogin();
      // Redirect to OIDC provider
      window.location.href = response.authorization_url;
    } catch (error) {
      throw error;
    }
  }, []);

  const handleCallback = useCallback(async (code, state) => {
    try {
      setIsLoading(true);

      const response = await authService.handleCallback(code, state);

      // Store tokens
      tokenStorage.setTokens(response);
      tenantEntryStorage.clear();
      setEnteredTenant(null);

      // Get user info
      const userInfo = await authService.getCurrentUser();
      setUser(userInfo);
      setIsAuthenticated(true);

      return response;
    } catch (error) {
      // Check for specific error types and provide helpful messages
      if (
        error.response?.status === 403 &&
        error.response?.data?.detail?.includes("role")
      ) {
        throw new Error(
          "Access Denied: You don't have the required permissions. Please contact your administrator to assign you the appropriate role."
        );
      } else if (error.response?.status === 500) {
        throw new Error(
          "Server Error: There was a problem with the authentication server. Please try again in a few moments."
        );
      } else if (error.response?.status === 400) {
        throw new Error(
          "Authentication Error: Invalid request. Please try logging in again from the main page."
        );
      } else if (error.code === "ERR_NETWORK") {
        throw new Error(
          "Network Error: Unable to connect to the authentication server. Please check your internet connection."
        );
      }

      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoggingOut(true);
      const token = tokenStorage.getAccessToken();
      if (token) {
        const response = await authService.logout();

        // Clear local state and tokens first
        tokenStorage.clearAll();
        localStorage.removeItem("grafana_authenticated");
        tenantEntryStorage.clear();
        setUser(null);
        setIsAuthenticated(false);
        setEnteredTenant(null);

        // Redirect to Zitadel logout page if URL is provided
        if (response.logout_url) {
          window.location.href = response.logout_url;
        }
      } else {
        // No token, just clear local state
        tokenStorage.clearAll();
        localStorage.removeItem("grafana_authenticated");
        tenantEntryStorage.clear();
        setUser(null);
        setIsAuthenticated(false);
        setEnteredTenant(null);
      }
    } catch (error) {
      // Even if logout fails, clear local state
      tokenStorage.clearAll();
      localStorage.removeItem("grafana_authenticated");
      tenantEntryStorage.clear();
      setUser(null);
      setIsAuthenticated(false);
      setEnteredTenant(null);
    } finally {
      setIsLoggingOut(false);
    }
  }, []);

  const enterTenant = useCallback(async (tenantId) => {
    const data = await tenantApi.enterTenant(tenantId);
    const tenant = { id: data.id, name: data.name };
    tenantEntryStorage.set(tenant);
    setEnteredTenant(tenant);
    return tenant;
  }, []);

  const exitTenant = useCallback(async () => {
    if (enteredTenant) {
      try {
        await tenantApi.exitTenant(enteredTenant.id);
      } catch (error) {
        console.warn("Failed to log tenant exit audit event", error);
      }
    }
    tenantEntryStorage.clear();
    setEnteredTenant(null);
  }, [enteredTenant]);

  const getAccessToken = useCallback(() => {
    return tokenStorage.getAccessToken();
  }, []);

  const hasRole = useCallback(
    (role) => user?.roles?.includes(role) || false,
    [user]
  );

  const isAdmin = useCallback(() => hasRole("admin"), [hasRole]);

  const isUser = useCallback(() => hasRole("user"), [hasRole]);

  const isSuperAdmin = useCallback(() => hasRole("super_admin"), [hasRole]);

  // True while a super_admin is acting inside an entered tenant, or for a
  // real tenant admin. This is the ONLY place elevation is computed —
  // user.roles itself is never mutated (spec §5).
  const isEffectivelyAdmin = useCallback(
    () => isAdmin() || (isSuperAdmin() && !!enteredTenant),
    [isAdmin, isSuperAdmin, enteredTenant]
  );

  // True for a super_admin who has NOT entered a tenant — today's
  // "platform-only, no tenant content" behavior. Components that special-case
  // isSuperAdmin() to skip tenant-scoped work (chat loading, dashboards,
  // configs) should gate on this instead, so entering a tenant makes them
  // behave like a normal admin again (Task 3 of this plan).
  const isPlatformOnlySuperAdmin = useCallback(
    () => isSuperAdmin() && !enteredTenant,
    [isSuperAdmin, enteredTenant]
  );

  // Effective roles for advisory permission derivation elsewhere in the app
  // (e.g. editableConfigTypesFor) — mirrors the backend's own elevation
  // (get_current_user adds "admin" to a COPY of roles for RBAC checks).
  // Never written back to user.roles. Always an array, elevated or not, so
  // callers never have to special-case an undefined roles list.
  const effectiveRoles = useCallback(
    () =>
      isEffectivelyAdmin()
        ? [...new Set([...(user?.roles ?? []), "admin"])]
        : [...(user?.roles ?? [])],
    [isEffectivelyAdmin, user]
  );

  // All-access = admin only (advisory mirror of backend has_all_access), OR an
  // entered super_admin acting as this tenant's admin (RPAA-213) — exactly
  // isEffectivelyAdmin. Aliased (not reimplemented) so the two can never
  // silently diverge for callers that gate on one or the other.
  const hasAllAccess = isEffectivelyAdmin;

  const isManager = useCallback(() => rolesIsManager(user?.roles), [user]);

  // section: a content section role key, or MANAGER for manager-tier surfaces.
  const canSeeSection = useCallback(
    (section) => isEffectivelyAdmin() || rolesCanSeeSection(user?.roles, section),
    [isEffectivelyAdmin, user]
  );

  const isInternalProceduresManager = useCallback(
    () => isEffectivelyAdmin() || rolesIsInternalProceduresManager(user?.roles),
    [isEffectivelyAdmin, user]
  );

  const canManageCollection = useCallback(
    (collection) => isEffectivelyAdmin() || rolesCanManageCollection(user?.roles, collection),
    [isEffectivelyAdmin, user]
  );

  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      isLoggingOut,
      login,
      logout,
      handleCallback,
      getAccessToken,
      hasRole,
      isAdmin,
      isUser,
      isSuperAdmin,
      hasAllAccess,
      isManager,
      canSeeSection,
      isInternalProceduresManager,
      canManageCollection,
      internalProceduresAccess,
      enteredTenant,
      enterTenant,
      exitTenant,
      isEffectivelyAdmin,
      isPlatformOnlySuperAdmin,
      effectiveRoles,
    }),
    [
      user,
      isAuthenticated,
      isLoading,
      isLoggingOut,
      login,
      logout,
      handleCallback,
      getAccessToken,
      hasRole,
      isAdmin,
      isUser,
      isSuperAdmin,
      hasAllAccess,
      isManager,
      canSeeSection,
      isInternalProceduresManager,
      canManageCollection,
      internalProceduresAccess,
      enteredTenant,
      enterTenant,
      exitTenant,
      isEffectivelyAdmin,
      isPlatformOnlySuperAdmin,
      effectiveRoles,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
